import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface ExportHistory {
  id: string;
  fileName: string;
  date: string;
  format: "csv" | "xlsx" | "pdf";
  productCount: number;
  size: string;
}

const MOCK_HISTORY: ExportHistory[] = [
  { id: "1", fileName: "all_products_2025-02-25.csv", date: "2025-02-25 14:30", format: "csv", productCount: 245, size: "1.2 MB" },
  { id: "2", fileName: "electronics_catalog.xlsx", date: "2025-02-20 09:15", format: "xlsx", productCount: 45, size: "856 KB" },
  { id: "3", fileName: "inventory_report.pdf", date: "2025-02-18 16:45", format: "pdf", productCount: 150, size: "2.1 MB" },
];

export default function ExportSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [exportFormat, setExportFormat] = useState<"csv" | "xlsx" | "pdf">("csv");
  const [selectedFields, setSelectedFields] = useState<string[]>(["name", "sku", "price", "stock", "category"]);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isExporting, setIsExporting] = useState(false);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const availableFields = [
    { id: "name", label: t("posProducts.export.fields.name") },
    { id: "sku", label: "SKU" },
    { id: "barcode", label: t("posProducts.export.fields.barcode") },
    { id: "price", label: t("posProducts.export.fields.price") },
    { id: "cost", label: t("posProducts.export.fields.cost") },
    { id: "stock", label: t("posProducts.export.fields.stock") },
    { id: "category", label: t("posProducts.export.fields.category") },
    { id: "description", label: t("posProducts.export.fields.description") },
    { id: "status", label: t("posProducts.export.fields.status") },
    { id: "visibility", label: t("posProducts.export.fields.visibility") },
  ];

  const toggleField = (fieldId: string) => {
    setSelectedFields((prev) =>
      prev.includes(fieldId) ? prev.filter((f) => f !== fieldId) : [...prev, fieldId]
    );
  };

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
    }, 2000);
  };

  const getFormatIcon = (format: ExportHistory["format"]) => {
    const icons = {
      csv: "ri-file-text-line text-emerald-500",
      xlsx: "ri-file-excel-2-line text-primary-500",
      pdf: "ri-file-pdf-line text-red-500",
    };
    return icons[format];
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posProducts.export.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posProducts.export.subtitle")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
            <h3 className={`font-semibold ${themeClasses.text} mb-4`}>{t("posProducts.export.format")}</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: "csv" as const, label: "CSV", icon: "ri-file-text-line", desc: t("posProducts.export.csvDesc") },
                { id: "xlsx" as const, label: "Excel", icon: "ri-file-excel-2-line", desc: t("posProducts.export.xlsxDesc") },
                { id: "pdf" as const, label: "PDF", icon: "ri-file-pdf-line", desc: t("posProducts.export.pdfDesc") },
              ].map((format) => (
                <button
                  key={format.id}
                  onClick={() => setExportFormat(format.id)}
                  className={`p-4 rounded-xl border transition-all text-left ${
                    exportFormat === format.id
                      ? "border-primary-500 bg-primary-500/10"
                      : `${themeClasses.border} ${themeClasses.tableRowHover}`
                  }`}
                >
                  <i className={`${format.icon} text-2xl ${exportFormat === format.id ? "text-primary-500" : themeClasses.textSecondary}`}></i>
                  <p className={`font-medium ${themeClasses.text} mt-2`}>{format.label}</p>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>{format.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
            <h3 className={`font-semibold ${themeClasses.text} mb-4`}>{t("posProducts.export.selectFields")}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {availableFields.map((field) => (
                <button
                  key={field.id}
                  onClick={() => toggleField(field.id)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                    selectedFields.includes(field.id)
                      ? "border-primary-500 bg-primary-500/10 text-primary-500"
                      : `${themeClasses.border} ${themeClasses.text} ${themeClasses.tableRowHover}`
                  }`}
                >
                  <i className={`${selectedFields.includes(field.id) ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"} mr-1`}></i>
                  {field.label}
                </button>
              ))}
            </div>
          </div>

          <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
            <h3 className={`font-semibold ${themeClasses.text} mb-4`}>{t("posProducts.export.filters")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm ${themeClasses.textSecondary} mb-2`}>
                  {t("posProducts.export.filterByCategory")}
                </label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className={`w-full px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text}`}
                >
                  <option value="all">{t("posProducts.export.allCategories")}</option>
                  <option value="electronics">Electronics</option>
                  <option value="clothing">Clothing</option>
                  <option value="food">Food & Beverages</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm ${themeClasses.textSecondary} mb-2`}>
                  {t("posProducts.export.filterByStatus")}
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className={`w-full px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text}`}
                >
                  <option value="all">{t("posProducts.allStatuses")}</option>
                  <option value="active">{t("posProducts.statusActive")}</option>
                  <option value="inactive">{t("posProducts.statusInactive")}</option>
                  <option value="out_of_stock">{t("posProducts.statusOutOfStock")}</option>
                </select>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border} flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <i className="ri-information-line text-blue-500 text-xl"></i>
              <div>
                <p className={`font-medium ${themeClasses.text}`}>{t("posProducts.export.exportSummary")}</p>
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  245 {t("posProducts.export.productsToExport")} • {selectedFields.length} {t("posProducts.export.fieldsSelected")}
                </p>
              </div>
            </div>
            <button
              onClick={handleExport}
              disabled={isExporting || selectedFields.length === 0}
              className={`px-6 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2 ${
                isExporting || selectedFields.length === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isExporting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {t("posProducts.export.exporting")}
                </>
              ) : (
                <>
                  <i className="ri-download-line"></i>
                  {t("posProducts.export.exportNow")}
                </>
              )}
            </button>
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4 h-fit`}>
          <h3 className={`font-semibold ${themeClasses.text} mb-4`}>{t("posProducts.export.history")}</h3>
          <div className="space-y-3">
            {MOCK_HISTORY.map((item) => (
              <div key={item.id} className={`p-3 rounded-xl ${themeClasses.cardBg}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <i className={getFormatIcon(item.format)}></i>
                    <span className={`text-sm font-medium ${themeClasses.text} truncate max-w-[150px]`}>
                      {item.fileName}
                    </span>
                  </div>
                  <button aria-label={t("general.download", { ns: "common" })} className="p-1 rounded hover:bg-primary-500/20">
                    <i className="ri-download-line text-primary-500"></i>
                  </button>
                </div>
                <div className={`text-xs ${themeClasses.textSecondary}`}>
                  <p>{item.date}</p>
                  <p>{item.productCount} products • {item.size}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
